// The reservation desk's logic, shared by the back office (pages/backoffice/
// ReservationDesk.jsx) and the dining Floor screen (apps/dining/screens/
// ReservationsPanel.jsx). Each screen draws it in its own design; the rules,
// the calls and the form live here once.
//
// `client` is the screen's own way to call /api/table-management:
//   { get(path, query), post(path, body), patch(path, body) }, path like "/reservations".
// Free times and tables always come from GET /reservations/availability (the
// server's rules); nothing here decides whether a table is free.
import { useCallback, useEffect, useMemo, useState } from 'react';

export const OCCASIONS = ['Birthday', 'Anniversary', 'Date night', 'Business', 'Celebration'];
export const COUNTRY_CODES = [['+1', 'US / CA'], ['+44', 'UK'], ['+52', 'MX'], ['+61', 'AU'], ['+33', 'FR'], ['+49', 'DE'], ['+91', 'IN'], ['+84', 'VN']];
export const OPEN_STATUSES = ['pending', 'confirmed'];
export const CLOSED_STATUSES = ['cancelled', 'no_show'];

export function hhmm12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export function dayLabel(iso, today) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  const label = d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  return iso === today ? `Today · ${label}` : label;
}

export function shiftDay(iso, days) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "+1" + "(555) 867-5309" -> "+15558675309"; a number typed with its own "+" is kept. */
export function e164(code, typed) {
  const raw = (typed || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (raw.startsWith('+')) return `+${digits}`;
  if (code === '+1' && digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `${code}${digits}`;
}

export function splitPhone(phone) {
  const found = COUNTRY_CODES.map(([c]) => c).sort((a, b) => b.length - a.length).find((c) => (phone || '').startsWith(c));
  return found ? [found, phone.slice(found.length)] : ['+1', phone || ''];
}

/** What staff may do with a booking from the list, in button order. */
export function reservationActions(r, { canEdit = true } = {}) {
  const open = OPEN_STATUSES.includes(r.status);
  const out = [];
  if (r.status === 'pending') out.push({ status: 'confirmed', label: 'Confirm', tone: 'primary' });
  if (open) out.push({ status: 'seated', label: 'Seat', tone: 'seat' });
  if (open && canEdit) out.push({ edit: true, label: 'Edit', tone: 'neutral' });
  if (r.status === 'confirmed') out.push({ status: 'no_show', label: 'No-show', tone: 'neutral', ask: `Mark ${r.customer_name} as a no-show?` });
  if (open) out.push({ status: 'cancelled', label: 'Cancel', tone: 'danger', ask: `Cancel ${r.customer_name}'s reservation?` });
  if (r.status === 'seated') out.push({ status: 'completed', label: 'Complete', tone: 'neutral' });
  if (CLOSED_STATUSES.includes(r.status)) out.push({ status: 'confirmed', label: 'Restore', tone: 'neutral', ask: 'Put this reservation back on the book? Its table must still be free.' });
  return out;
}

export function statusDoneText(r, status) {
  return {
    seated: `${r.customer_name} seated${r.table_number ? ` at table ${r.table_number}` : ''}`,
    no_show: `${r.customer_name} marked as a no-show`,
    cancelled: `${r.customer_name}'s reservation cancelled`,
    confirmed: `${r.customer_name}'s reservation confirmed`,
    completed: `${r.customer_name}'s reservation completed`,
  }[status];
}

/** The text a screen shows after a save: the server's own answer on the confirmation text. */
export function savedText(answer, editing) {
  const r = answer.reservation;
  const what = `${r.customer_name} · ${r.party_size} at ${hhmm12(r.time)}${r.table_number ? ` · table ${r.table_number}` : ''}`;
  if (answer.sms?.requested && !answer.sms.sent) {
    return { ok: false, text: `${editing ? 'Saved' : 'Booked'}: ${what}. The confirmation text was NOT sent: ${answer.sms.error}` };
  }
  return { ok: true, text: `${editing ? 'Saved' : 'Booked'}: ${what}${answer.sms?.sent ? ' · confirmation texted' : ''}` };
}

/** One day of the book: the restaurant's today and time zone, the chosen day, its reservations. */
export function useReservationDay(client, { refreshKey } = {}) {
  const [day, setDay] = useState(null);
  const [today, setToday] = useState(null);
  const [zone, setZone] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // the restaurant's today (never the device's)
  useEffect(() => {
    const probe = new Date().toISOString().slice(0, 10);
    client.get('/reservations/availability', { date: probe, party_size: 1 })
      .then((a) => { setToday(a.today); setZone(a.timezone); setDay((d) => d || a.today); })
      .catch((e) => { setError(e.message); setDay((d) => d || probe); });
  }, [client]);

  const load = useCallback(async () => {
    if (!day) return;
    try {
      setData(await client.get('/reservations', { date: day }));
      setError(null);
    } catch (e) { setError(e.message); }
  }, [client, day]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const setStatus = useCallback((r, status) => client.patch(`/reservations/${r.id}/status?status=${status}`), [client]);

  const list = (data?.reservations || []).filter((r) => r.status !== 'pending_verification');
  const active = list.filter((r) => !CLOSED_STATUSES.includes(r.status));
  const closed = list.filter((r) => CLOSED_STATUSES.includes(r.status));
  return {
    day, setDay, today, zone, data, error, load, setStatus, active, closed,
    guests: active.reduce((s, r) => s + (r.party_size || 0), 0),
  };
}

/** The New / Edit reservation form: fields, the server's free times and tables, and save. */
export function useReservationForm({ client, reservation, defaultDate, onSaved }) {
  const editing = !!reservation;
  const [form, setForm] = useState(() => {
    const [code, number] = splitPhone(reservation?.customer_phone);
    return {
      name: reservation?.customer_name || '',
      code,
      phone: editing ? number : '',
      email: reservation?.customer_email || '',
      party: reservation?.party_size || 2,
      date: reservation?.date || defaultDate || '',
      time: reservation?.time || '',
      tableId: reservation?.table_id ?? null,
      occasion: reservation?.occasion || '',
      allergies: reservation?.allergies || '',
      notes: reservation?.special_requests || '',
      text: false,
    };
  });
  const [avail, setAvail] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const set = useCallback((patch) => { setForm((f) => ({ ...f, ...patch })); setProblem(null); }, []);
  useEffect(() => { if (!form.date && defaultDate) setForm((f) => ({ ...f, date: defaultDate })); }, [defaultDate, form.date]);

  const query = useCallback((f) => {
    const q = { date: f.date, party_size: f.party };
    if (f.time) q.time = f.time;
    if (editing) q.exclude_reservation_id = reservation.id;
    return q;
  }, [editing, reservation]);

  useEffect(() => {
    if (!form.date) return undefined;
    let live = true;
    setLoadingAvail(true);
    client.get('/reservations/availability', query(form))
      .then((a) => { if (live) setAvail(a); })
      .catch((e) => { if (live) { setAvail(null); setProblem(e.message); } })
      .finally(() => { if (live) setLoadingAvail(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, form.date, form.party, form.time, query]);

  const slots = avail?.slots || [];
  const freeTables = useMemo(() => (avail?.tables || []).filter((t) => t.fits_party && t.free), [avail]);
  // a picked table no longer free for the slot is dropped (the server re-checks anyway)
  useEffect(() => {
    if (form.tableId != null && avail?.tables?.length && !freeTables.some((t) => t.id === form.tableId)) {
      setForm((f) => ({ ...f, tableId: null }));
    }
  }, [avail, freeTables, form.tableId]);

  const phone = e164(form.code, form.phone);
  const digits = phone.replace(/\D/g, '').length;
  const phoneOk = digits >= 10 && digits <= 15;
  const slotFree = !form.time || !slots.length || slots.some((s) => s.time === form.time && s.tables_available > 0);
  const ready = !!(form.name.trim() && phoneOk && form.party >= 1 && form.date && form.time) && slotFree;

  const save = async () => {
    setSaving(true);
    setProblem(null);
    const body = {
      customer_name: form.name.trim(), customer_phone: phone, customer_email: form.email.trim() || null,
      party_size: Number(form.party), date: form.date, time: form.time, table_id: form.tableId,
      notes: form.notes.trim() || null, occasion: form.occasion.trim() || null, allergies: form.allergies.trim() || null,
      text_confirmation: form.text,
    };
    try {
      let answer;
      if (editing) {
        const was = {
          customer_name: reservation.customer_name, customer_phone: reservation.customer_phone,
          customer_email: reservation.customer_email || null, party_size: reservation.party_size, date: reservation.date,
          time: reservation.time, table_id: reservation.table_id, notes: reservation.special_requests || null,
          occasion: reservation.occasion || null, allergies: reservation.allergies || null,
        };
        const changed = { text_confirmation: form.text };
        Object.keys(was).forEach((k) => { if (body[k] !== was[k]) changed[k] = body[k]; });
        answer = await client.patch(`/reservations/${reservation.id}`, changed);
      } else {
        answer = await client.post('/reservations', body);
      }
      setSaving(false);
      onSaved(answer);
      return answer;
    } catch (e) {
      setProblem(e.message);
      setSaving(false);
      // a table taken meanwhile: show the fresh picture
      if (form.time) client.get('/reservations/availability', query(form)).then(setAvail).catch(() => {});
      return null;
    }
  };

  return {
    editing, form, set, avail, loadingAvail, slots, freeTables, phone, phoneOk, slotFree, ready, saving, problem, save,
  };
}
